import { skipToken, type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
  useGetAdvisorInvestorByIdQuery,
  useGetAdvisorInvestorsQuery,
} from '@/store/usersApi';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';

export type AdvisorClientAccessState =
  | 'loading'
  | 'authorized'
  | 'forbidden'
  | 'notFound'
  | 'error';

export interface AdvisorClientAccess {
  state: AdvisorClientAccessState;
  isLoading: boolean;
  isAuthorized: boolean;
  isForbidden: boolean;
  isNotFound: boolean;
  isError: boolean;
}

export const useAdvisorClientAccess = (
  investorId?: number,
): AdvisorClientAccess => {
  const authLoaded = useAuthIsLoaded();
  const canQuery = typeof investorId === 'number' && authLoaded;

  const { data: listData, isLoading: isListLoading } =
    useGetAdvisorInvestorsQuery(canQuery ? {} : skipToken);

  const foundInList = Boolean(
    canQuery &&
    listData?.Records.some((investor) => investor.InvestorId === investorId),
  );

  const byIdArg = canQuery ? investorId : skipToken;
  const {
    currentData: byIdData,
    isLoading: isByIdLoading,
    isError: isByIdError,
    error: byIdError,
  } = useGetAdvisorInvestorByIdQuery(byIdArg);

  const byIdStatus = isByIdError
    ? (byIdError as FetchBaseQueryError).status
    : undefined;

  let state: AdvisorClientAccessState = 'loading';
  if (!canQuery) {
    state = 'loading';
  } else if (byIdStatus === 403) {
    state = 'forbidden';
  } else if (byIdStatus === 404) {
    state = 'notFound';
  } else if (foundInList || byIdData) {
    state = 'authorized';
  } else if (isListLoading || isByIdLoading) {
    state = 'loading';
  } else {
    // Both queries have settled with nothing to show: either isByIdError
    // (a non-HTTP-status error) or a client absent from the cached list
    // whose by-id probe resolved without one (e.g. a successful null body).
    // Neither case is a confirmed 404, so surface a recoverable error
    // instead of leaving state at its 'loading' default forever.
    state = 'error';
  }

  return {
    state,
    isLoading: state === 'loading',
    isAuthorized: state === 'authorized',
    isForbidden: state === 'forbidden',
    isNotFound: state === 'notFound',
    isError: state === 'error',
  };
};
